import { useEffect, useMemo, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  line, MAP_VIEW_HEIGHT_PX, Size, fitPins, panCenter, pinOffsets, recordHref, staticMapQuery, staticMapSize, t, tileGrid, zoomed,
  type MapCenter, type MapPin, type MapSize, type MappableRecord, type MapsProvider,
} from '@bms2/shared';
import { Button } from '../../components/controls';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { PAGES_PATH, useApi } from './api';
import styles from './MapView.module.css';

/** 🔨 Relative to the API's own root, never prefixed with /api (piece 1); the browser asks the API for every map picture and no map host (decision 0343). */
export const MAPS_TILE_PATH = '/maps/tile';
export const MAPS_STATIC_PATH = '/maps/static';
/** Where a map service is chosen, named by the Off state. */
export const MAPS_SETTINGS_ROUTE = '/settings/company/maps';

export interface MapAnswer {
  provider: MapsProvider;
  pins: MapPin[];
  /** The rows on the list that carry no coordinates: counted under the map, never guessed at. */
  unplaced: number;
}

/**
 * The Map view of the Locations and Sites lists (piece 13; decision 0343):
 * one pin per row with coordinates over the picture the chosen map service
 * draws. OpenStreetMap and Geoapify are a grid of raster tiles (decision 7510)
 * and Google Maps one static image, each fetched through the API so the key
 * stays on the server and the proxy's policy stays at `img-src 'self'`. 🔨 No map library: the pins are
 * placed by the shared Web Mercator arithmetic, and the picture pans by drag
 * and zooms in steps. A pin opens its record.
 */
export function MapView({ recordType, apiBaseUrl = '', term = '' }: { recordType: MappableRecord; apiBaseUrl?: string; term?: string }) {
  const { state, reload } = useApi<MapAnswer>(`${PAGES_PATH}/list/${recordType}/map${term ? `?q=${encodeURIComponent(term)}` : ''}`, apiBaseUrl);
  return (
    <AsyncStatePanel state={state} emptyAction={t('map.empty')} onRetry={reload}>
      {(answer) => <MapCanvas key={`${answer.provider}:${answer.pins.map((pin) => pin.id).join(',')}`} answer={answer} recordType={recordType} apiBaseUrl={apiBaseUrl} />}
    </AsyncStatePanel>
  );
}

interface Drag {
  startX: number;
  startY: number;
  dx: number;
  dy: number;
}

function MapCanvas({ answer, recordType, apiBaseUrl }: { answer: MapAnswer; recordType: MappableRecord; apiBaseUrl: string }) {
  const navigate = useNavigate();
  const [frame, setFrame] = useState<HTMLDivElement | null>(null);
  const [measured, setMeasured] = useState<MapSize | null>(null);
  /**
   * The map measures the stage that holds it the way the board does (decision 0327); unmeasured, as under jsdom, it draws at the
   * stylesheet's own size. 🔨 The stage's size is the stylesheet's and the canvas takes it, so what is measured is never what the
   * measure sets: Google's canvas once carried the measured size as its own inline width, the observer read the content box inside
   * the border, and the canvas lost two pixels a pass until it was a sliver (decision 7510; T3103).
   */
  useEffect(() => {
    if (!frame || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect && rect.width > 0) setMeasured({ width: Math.round(rect.width), height: Math.round(rect.height) || MAP_VIEW_HEIGHT_PX });
    });
    observer.observe(frame);
    return () => observer.disconnect();
  }, [frame]);
  const google = answer.provider === 'GoogleMaps';
  const size = useMemo<MapSize>(() => {
    const own = measured ?? { width: MAP_VIEW_HEIGHT_PX * 2, height: MAP_VIEW_HEIGHT_PX };
    return google ? staticMapSize(own) : own;
  }, [measured, google]);
  const fitted = useMemo(() => fitPins(answer.pins, size), [answer.pins, size]);
  const [moved, setMoved] = useState<MapCenter | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const center = moved ?? fitted;

  if (answer.provider === 'Off') {
    return (
      <p className={styles.hint} data-map-off="true">
        {t('map.off')}
        {' '}
        <Link to={MAPS_SETTINGS_ROUTE}>{t('map.settingsLink')}</Link>
      </p>
    );
  }
  if (!center) {
    return <p className={styles.hint} data-map-empty={answer.unplaced}>{t('map.empty')}</p>;
  }

  const down = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDrag({ startX: event.clientX, startY: event.clientY, dx: 0, dy: 0 });
  };
  const move = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (drag) setDrag({ ...drag, dx: event.clientX - drag.startX, dy: event.clientY - drag.startY });
  };
  const up = () => {
    if (drag && (drag.dx !== 0 || drag.dy !== 0)) setMoved(panCenter(center, drag.dx, drag.dy));
    setDrag(null);
  };
  const pins = pinOffsets(answer.pins, center, size);
  const shift = drag ? `translate(${drag.dx}px, ${drag.dy}px)` : undefined;

  return (
    <div className={styles.frame} data-map-view={answer.provider} data-map-zoom={center.zoom}>
      <div className={styles.toolbar}>
        <Button size={Size.Small} text={[t('map.zoomIn')]} actions={[() => setMoved(zoomed(center, 1))]} />
        <Button size={Size.Small} text={[t('map.zoomOut')]} actions={[() => setMoved(zoomed(center, -1))]} />
        <span className={styles.hint}>{t('map.dragHint')}</span>
      </div>
      <div ref={setFrame} className={styles.stage} data-map-stage="true">
        <div
          className={drag ? styles.canvasDragging : styles.canvas}
          style={google ? { width: size.width, height: size.height } : undefined}
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerCancel={() => setDrag(null)}
          data-map-canvas={google ? 'static' : 'tiles'}
        >
          <div className={styles.layer} style={shift ? { transform: shift } : undefined} aria-hidden="true">
            {google ? (
              <img className={styles.staticImage} src={`${apiBaseUrl}${MAPS_STATIC_PATH}?${staticMapQuery(center, size)}`} alt={t('map.alt')} width={size.width} height={size.height} draggable={false} data-map-static="true" />
            ) : (
              tileGrid(center, size).map((tile) => (
                <img key={`${tile.z}/${tile.x}/${tile.y}`} className={styles.tile} src={`${apiBaseUrl}${MAPS_TILE_PATH}/${tile.z}/${tile.x}/${tile.y}`} alt={t('map.alt')} style={{ left: tile.left, top: tile.top }} draggable={false} data-map-tile={`${tile.z}/${tile.x}/${tile.y}`} />
              ))
            )}
          </div>
          <div className={styles.layer} style={shift ? { transform: shift } : undefined}>
            {pins.map(({ pin, left, top }) => (
              <button
                key={pin.id}
                type="button"
                className={styles.pin}
                style={{ left, top }}
                title={pin.label}
                aria-label={pin.label}
                data-map-pin={pin.id}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={() => navigate(recordHref(recordType, pin.id))}
              />
            ))}
          </div>
        </div>
      </div>
      <div className={styles.toolbar}>
        {answer.unplaced > 0 ? <span className={styles.hint} data-map-unplaced={answer.unplaced}>{line(answer.unplaced, t('map.unplaced'))}</span> : null}
        {google ? null : <span className={styles.attribution} data-map-attribution={answer.provider}>{t(answer.provider === 'Geoapify' ? 'map.attributionGeoapify' : 'map.attribution')}</span>}
      </div>
    </div>
  );
}
